import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Lenis from 'lenis';

const MainPage = () => {
  const navigate = useNavigate();
  const centerButtonRef = useRef(null);
  const topRectangleRef = useRef(null);
  const bottomRectangleRef = useRef(null);
  const headlineRef = useRef(null);
  const upperHeadlineRef = useRef(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    // Initialize Lenis for smooth scrolling
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    // RAF loop for Lenis
    function raf(time) {
      lenisRef.current.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    const centerButton = centerButtonRef.current;
    const topRectangle = topRectangleRef.current;
    const bottomRectangle = bottomRectangleRef.current;

    const handleMouseOver = () => {
      centerButton.textContent = "Let's roll";
    };

    const handleClick = () => {
      topRectangle.style.height = '50%';
      bottomRectangle.style.height = '50%';
      
      setTimeout(() => {
        document.body.classList.add('full-screen');
      }, 2000);
      
      setTimeout(() => {
        navigate('/portfolio');
      }, 2000);
    };

    centerButton.addEventListener('mouseover', handleMouseOver);
    centerButton.addEventListener('click', handleClick);

    // Initialize cursor spotlight effect
    const initCursorSpotlight = () => {
      // Only create spotlight on desktop devices
      if (window.innerWidth <= 768) return;
      
      const spotlight = document.createElement("div");
      spotlight.className = "cursor-spotlight";
      spotlight.style.cssText = `
        position: fixed;
        width: 600px;
        height: 600px;
        border-radius: 50%;
        pointer-events: none;
        background: radial-gradient(circle,
          rgba(132, 0, 255, 0.15) 0%,
          rgba(132, 0, 255, 0.08) 15%,
          rgba(132, 0, 255, 0.04) 25%,
          rgba(132, 0, 255, 0.02) 40%,
          rgba(132, 0, 255, 0.01) 65%,
          transparent 70%
        );
        z-index: 1000;
        opacity: 0;
        transform: translate(-50%, -50%);
        mix-blend-mode: screen;
        transition: opacity 0.3s ease;
      `;
      document.body.appendChild(spotlight);

      const handleMouseMove = (e) => {
        spotlight.style.left = e.clientX + 'px';
        spotlight.style.top = e.clientY + 'px';
        spotlight.style.opacity = '1';
      };

      const handleMouseLeave = () => {
        spotlight.style.opacity = '0';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseleave', handleMouseLeave);
        if (spotlight.parentNode) {
          spotlight.parentNode.removeChild(spotlight);
        }
      };
    };

    const cleanupSpotlight = initCursorSpotlight();

    return () => {
      centerButton.removeEventListener('mouseover', handleMouseOver);
      centerButton.removeEventListener('click', handleClick);
      if (typeof cleanupSpotlight === 'function') {
        cleanupSpotlight();
      }
      // Ensure we never leave the app with a forced black background
      document.body.classList.remove('full-screen');
      
      // Cleanup Lenis
      if (lenisRef.current) {
        lenisRef.current.destroy();
      }
    };
  }, [navigate]);

  return (
    <div className="main-page-container">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Boldonse:wght@400&display=swap');
          
          .main-page-container {
            margin: 0;
            padding: 0;
            font-family: 'Boldonse', system-ui, sans-serif;
            background-color: white;
            overflow: hidden;
            height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
          }

          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .main-page-container {
              padding: 20px;
              height: 100vh;
              min-height: 100vh;
            }
          }
          
          .center-button {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: black;
            color: white;
            border-radius: 50px;
            width: 400px;
            height: 90px;
            border: 4px solid rgba(132, 0, 255, 1);
            text-decoration: none;
            font-family: 'Boldonse', system-ui, sans-serif;
            font-size: 22px;
            font-weight: bold;
            transition: all 0.3s ease-in-out;
            cursor: pointer;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            z-index: 2;
            overflow: hidden;
            padding: 0 25px;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4), 0 4px 10px rgba(0, 0, 0, 0.3);
          }

          /* Mobile Button Styles */
          @media (max-width: 768px) {
            .center-button {
              width: 90%;
              max-width: 350px;
              height: 75px;
              font-size: 18px;
              padding: 0 20px;
              border-width: 3px;
            }
          }

          @media (max-width: 480px) {
            .center-button {
              width: 95%;
              max-width: 300px;
              height: 70px;
              font-size: 16px;
              padding: 0 15px;
              border-width: 2px;
            }
          }

          .center-button:hover {
            background-color: white;
            color: black;
          }

          .headline {
            position: absolute;
            bottom: 10%;
            white-space: nowrap;
            font-size: 90px;
            font-weight: bold;
            color: black;
            margin-top: 30px;
            overflow: hidden;
            font-family: 'Boldonse', system-ui, sans-serif;
            text-shadow: 3px 3px 8px rgba(0, 0, 0, 0.3), 1px 1px 4px rgba(0, 0, 0, 0.2);
            will-change: transform;
            transition: transform 0.1s ease-out;
            animation: scrollHeadline 8s linear infinite;
          }

          /* Mobile Headline Styles */
          @media (max-width: 768px) {
            .headline {
              font-size: 60px;
              bottom: 25%;
              margin-top: 20px;
              white-space: nowrap;
              padding: 0 20px;
            }
          }

          @media (max-width: 480px) {
            .headline {
              font-size: 40px;
              bottom: 30%;
              margin-top: 15px;
              white-space: nowrap;
              padding: 0 15px;
            }
          }

          .Upperheadline {
            position: absolute;
            top: 10%;
            white-space: nowrap;
            font-size: 90px;
            font-weight: bold;
            color: black;
            margin-top: 30px;
            overflow: hidden;
            font-family: 'Boldonse', system-ui, sans-serif;
            text-shadow: 3px 3px 8px rgba(0, 0, 0, 0.3), 1px 1px 4px rgba(0, 0, 0, 0.2);
            will-change: transform;
            transition: transform 0.1s ease-out;
            animation: snakeCircle 5s linear infinite;
          }

          /* Mobile Upper Headline Styles */
          @media (max-width: 768px) {
            .Upperheadline {
              font-size: 60px;
              top: 25%;
              margin-top: 20px;
              white-space: nowrap;
              padding: 0 20px;
            }
          }

          @media (max-width: 480px) {
            .Upperheadline {
              font-size: 40px;
              top: 30%;
              margin-top: 15px;
              white-space: nowrap;
              padding: 0 15px;
            }
          }

          .center-button span {
            position: relative;
            display: inline-block;
            max-width: 150px;
            overflow: hidden;
          }

          /* Mobile Span Styles */
          @media (max-width: 768px) {
            .center-button span {
              max-width: 120px;
            }
          }

          @media (max-width: 480px) {
            .center-button span {
              max-width: 100px;
            }
          }

          .center-button span::before {
            content: "Developer";
            background: linear-gradient(90deg, rgba(132, 0, 255, 1), rgba(243, 22, 81, 1));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: words 20s infinite;
            white-space: nowrap;
          }

          .main-logo {
            display: none;
          }

          .center-button span::after {
            content: "";
            position: absolute;
            width: calc(100% + 8px);
            height: 100%;
            background-color: black;
            border-left: 2px solid white;
            right: -8px;
            animation: cursor .8s infinite, typing 20s steps(14) infinite;
          }

          @keyframes cursor {
            to {
              border-left: 3px solid rgba(132, 0, 255, 1);
            }
          }

          @keyframes words {
            0%, 20% {
              content: "Developer";
            }
            21%, 40% {
              content: "Student";
            }
            61%, 80% {
              content: "Learner";
            }
            81%, 100% {
              content: "Gamer";
            }
          }

          @keyframes typing {
            10%, 15%, 30%, 35%, 50%, 55%, 70%, 75%, 90%, 95% {
              width: 0;
            }
            5%, 20%, 25%, 40%, 45%, 60%, 65%, 80%, 85% {
              width: calc(100% + 8px);
            }
          }

          .top-rectangle,
          .bottom-rectangle {
            position: absolute;
            width: 100%;
            background-color: black;
            transition: height 1s ease;
            border: none;
            outline: none;
          }

          .top-rectangle {
            top: 0;
            height: 0;
          }

          .bottom-rectangle {
            bottom: 0;
            height: 0;
          }

          .full-screen {
            background-color: black !important;
          }

          /* Additional Mobile Optimizations */
          @media (max-width: 768px) {
            .main-page-container {
              -webkit-overflow-scrolling: touch;
              touch-action: manipulation;
            }
            
            .center-button {
              -webkit-tap-highlight-color: transparent;
              touch-action: manipulation;
            }
          }

          /* Landscape Mobile Adjustments */
          @media (max-width: 768px) and (orientation: landscape) {
            .headline {
              font-size: 50px;
              bottom: 20%;
            }
            
            .Upperheadline {
              font-size: 50px;
              top: 20%;
            }
            
            .center-button {
              height: 65px;
              font-size: 16px;
            }
          }

          @keyframes snakeCircle {
            from {
              transform: translateX(50%);
            }
            to {
              transform: translateX(-50%);
            }
          }

          @keyframes scrollHeadline {
            from {
              transform: translateX(50%);
            }
            to {
              transform: translateX(-50%);
            }
          }
        `}
      </style>
      
      <div className="top-rectangle" ref={topRectangleRef}></div>
      <div className="bottom-rectangle" ref={bottomRectangleRef}></div>
      
      <button ref={centerButtonRef} className="center-button">
        I'am a&nbsp;<span></span>
      </button>
      
      <div className="headline" ref={headlineRef}>
        <span className="mhead">
          I'm a 'web designer'....which means I can make things pretty, but I can't fix your Wi-Fi.😄
        </span>
      </div>
      
      <div className="Upperheadline" ref={upperHeadlineRef}>
        <span className="mhead">Welcome to my portfolio website</span>
      </div>
    </div>
  );
};

export default MainPage;